import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  HourglassEmpty,
  Save,
  Refresh,
} from '@mui/icons-material';
import { attendanceAPI } from '../../services/api';
import { format } from 'date-fns';

const AttendanceMarking = () => {
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedClass, setSelectedClass] = useState('');
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const classes = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];
  const statusOptions = ['PRESENT', 'ABSENT', 'LATE', 'HALF_DAY'];

  const loadAttendance = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const response = await attendanceAPI.getAttendance({
        date: selectedDate,
        standard: selectedClass,
      });
      
      const studentsData = response.data;
      setStudents(studentsData);
      
      // Initialize attendance state
      const initialAttendance = {};
      studentsData.forEach(student => {
        initialAttendance[student.id] = {
          student_id: student.id,
          status: student.status || 'PRESENT',
          remarks: student.remarks || '',
        };
      });
      setAttendance(initialAttendance);
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Failed to load students: ' + (error.response?.data?.message || error.message),
      });
    } finally {
      setLoading(false);
    }
  }, [selectedClass, selectedDate]);

  useEffect(() => {
    if (selectedClass && selectedDate) {
      loadAttendance();
    }
  }, [selectedClass, selectedDate, loadAttendance]);

  const handleStatusChange = (studentId, status) => {
    setAttendance(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        status,
      },
    }));
  };

  const handleRemarksChange = (studentId, remarks) => {
    setAttendance(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        remarks,
      },
    }));
  };

  const markAllPresent = () => {
    const updatedAttendance = {};
    students.forEach(student => {
      updatedAttendance[student.id] = {
        student_id: student.id,
        status: 'PRESENT',
        remarks: '',
      };
    });
    setAttendance(updatedAttendance);
  };

  const handleSubmit = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const records = Object.values(attendance);
      await attendanceAPI.markAttendance({
        attendance_date: selectedDate,
        records,
      });
      
      setMessage({
        type: 'success',
        text: `Attendance marked successfully for ${records.length} students!`,
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Failed to save attendance: ' + (error.response?.data?.message || error.message),
      });
    } finally {
      setSaving(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PRESENT':
        return <CheckCircle sx={{ color: 'success.main' }} />;
      case 'ABSENT':
        return <Cancel sx={{ color: 'error.main' }} />;
      case 'LATE':
        return <HourglassEmpty sx={{ color: 'warning.main' }} />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PRESENT':
        return 'success';
      case 'ABSENT':
        return 'error';
      case 'LATE':
        return 'warning';
      case 'HALF_DAY':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight="bold">
        Mark Attendance
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              label="Date"
              type="date"
              fullWidth
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Class/Standard</InputLabel>
              <Select
                value={selectedClass}
                label="Class/Standard"
                onChange={(e) => setSelectedClass(e.target.value)}
              >
                {classes.map((cls) => (
                  <MenuItem key={cls} value={cls}>
                    Class {cls}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                onClick={markAllPresent}
                disabled={!selectedClass || loading}
                fullWidth
              >
                Mark All Present
              </Button>
              <Button
                variant="outlined"
                onClick={loadAttendance}
                disabled={!selectedClass || loading}
                startIcon={<Refresh />}
              >
                Reload
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* Student List */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : students.length > 0 ? (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.50' }}>
                  <TableCell>S.No</TableCell>
                  <TableCell>Enrollment No</TableCell>
                  <TableCell>Student Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Remarks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {students.map((student, index) => (
                  <TableRow key={student.id} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{student.enrollment_no}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getStatusIcon(attendance[student.id]?.status)}
                        <Typography>{student.student_name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <ToggleButtonGroup
                        value={attendance[student.id]?.status || 'PRESENT'}
                        exclusive
                        onChange={(e, value) => {
                          if (value) handleStatusChange(student.id, value);
                        }}
                        size="small"
                      >
                        {statusOptions.map((status) => (
                          <ToggleButton key={status} value={status}>
                            <Chip
                              label={status}
                              size="small"
                              color={getStatusColor(status)}
                              variant={attendance[student.id]?.status === status ? 'filled' : 'outlined'}
                            />
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        placeholder="Add remarks..."
                        value={attendance[student.id]?.remarks || ''}
                        onChange={(e) => handleRemarksChange(student.id, e.target.value)}
                        fullWidth
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Summary */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <Box display="flex" gap={3}>
                  <Chip
                    label={`Total: ${students.length}`}
                    color="default"
                    variant="outlined"
                  />
                  <Chip
                    label={`Present: ${Object.values(attendance).filter(a => a.status === 'PRESENT').length}`}
                    color="success"
                  />
                  <Chip
                    label={`Absent: ${Object.values(attendance).filter(a => a.status === 'ABSENT').length}`}
                    color="error"
                  />
                  <Chip
                    label={`Late: ${Object.values(attendance).filter(a => a.status === 'LATE').length}`}
                    color="warning"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={handleSubmit}
                  disabled={saving || students.length === 0}
                  startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                >
                  {saving ? 'Saving...' : 'Save Attendance'}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {selectedClass
              ? 'No students found for this class'
              : 'Please select a date and class to mark attendance'}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default AttendanceMarking;
